import { XdrSearchView } from '../view/XdrSearchView';
import { templateXdrSearch } from '../view/template';
import { XdrSearchRepository } from '../repository/XdrSearchRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { XdrSearchWebComponent } from '..';
import { CODE_EDITOR_OPTIONS, RpcResponse, SearchEvents, SmartViewItem, XdrSearchWebComponentSchema } from '../types';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { GlobalExclusionsEvents } from '../../../configurationProfilesApp/exclusionsPanel/types';
import { SearchQueryPanelEvents } from '../../xdrSearchQueryPanel/types';
import { TimeRange, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { XdrSearchResultsDetailsPanelEvents } from '../../xdrSearchResultsPanel/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { htmlDecode, htmlEncode } from '../../../utils/escape.utils';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { SmartViewEvents } from '../../../grid/gridSmartViews/types';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';
import { MinLengthValidator } from '../../../ui/gzForm/validators/min-length.validator';
import { GridEvents } from '../../../grid/types/common';
import { TimeRangeEvents } from '../../../ui/gzDatetimeFilter/types';
import { ConsoleRequest, DataProviderType } from "../../../utils/request";
import { AvailableCompaniesSource } from '../../../../../../libs/shared/dtos/types/dashboards/src/lib/shared-dtos-dashboards-gz-console';

export class XdrSearchController extends BaseWebComponentController {
  public component: XdrSearchWebComponent;
  public dataRepository: XdrSearchRepository;
  public view: XdrSearchView;
  public viewConstructor: typeof XdrSearchView;
  public dataRepositoryConstructor: typeof XdrSearchRepository;

  constructor(component: XdrSearchWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: XdrSearchView,
      viewTemplate: templateXdrSearch,
      dataRepositoryConstructor: XdrSearchRepository,
    });
  }

  private selectedSmartViewItem: SmartViewItem | undefined;
  private activeTimeRange: TimeRange | null = {
    kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
    absolute: {
      fromTimestamp: new Date(Date.now() - 86400 * 1000).getTime(),
      toTimestamp: new Date().getTime(),
    },
  };

  private static sanitizeStringValue(value) {
    if (
      (value && typeof value === 'string' && (value.indexOf(' ') >= 0 || value.indexOf('/') >= 0)) ||
      String.raw`${value}`.indexOf('\\') >= 0
    ) {
      value = String.raw`${value}`.replace(/\\/g, '\\\\');
      value = value.replace(/"/g, '\\"');
      value = '"' + String.raw`${value}` + '"';
    }

    return value;
  }

  public closeSideMenu(): void {
    this.view.dom.$detailsPanel.closeSideMenu();
  }

  async getDataForView(): Promise<XdrSearchWebComponentSchema> {
    return this.dataRepository.getDataForView();
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$searchResultsPanel,
        event: GridEvents.SET_PAGE_SIZE,
        callback: this.updateGridSettings,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchEvents.RUN_QUERY,
        callback: this.onRunQuery,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.UPDATE_COMPLETE,
        callback: this.onUpdateComplete,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: XdrSearchResultsDetailsPanelEvents.REFINE_SEARCH_FROM_SIDE_PANEL,
        callback: this.onSidePanelFiltering,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.SAVE_AS,
        callback: this.onSaveAs,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.SAVE,
        callback: this.onSave,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.onClickSmartViewItem,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_DELETED,
        callback: this.onDeleteSmartViewItem,
      },
      {
        element: this.view.dom.$searchResultsPanel,
        event: GlobalExclusionsEvents.REFRESH_COMPONENT,
        callback: this.onCompanyChange,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: TimeRangeEvents.TIMERANGE_CHANGE,
        callback: this.onTimeRangeChange,
      },
    ];
  }

  public onTimeRangeChange(event) {
    this.activeTimeRange = event.detail.timeRange;
  }

  public updateGridSettings() {
    const gridView = this.view.dom.$searchResultsPanel.controller.view.dom.$gridView;
    gridView.controller.handleGridItemsLoad();
  }

  public async onSidePanelFiltering(event: WebComponentEvent) {
    const eventValue = XdrSearchController.sanitizeStringValue(event.detail.value);
    let queryString = event.detail.field + ':' + eventValue;
    const initialValue = await this.view.dom.$searchQueryPanel.searchQuery.codeEditor.getValue();

    if (initialValue.raw !== '') {
      queryString = initialValue.raw + ' ' + event.detail.operator + ' ' + queryString;
    }

    this.view.dom.$searchQueryPanel.searchQuery.codeEditor.setValue(queryString);
  }

  async onRunQuery(event: WebComponentEvent) {
    this.view.removeElementClass(this.view.dom.$searchResultsPanel.controller.view.dom.$gridView, 'hidden');
    this.view.setElementClass(
      this.view.dom.$searchResultsPanel.controller.view.dom.$gridView.controller.view.dom.$wrapper,
      'more-items-loading'
    );
    this.view.setElementClass(
      this.view.dom.$searchResultsPanel.controller.view.dom.$gridView.controller.view.dom.$wrapper,
      'is-summary-loading'
    );

    if (event.detail.updateInput) {
      this.view.dom.$searchQueryPanel.searchQuery.setValue(event.detail.input);
    }

    const query: string = event.detail.input ? event.detail.input : 'other.event_name:*';
    const activeTimeRange = this.view.dom.$searchQueryPanel.datetimeFilter.activeTimeRange;
    const lastNumDays = activeTimeRange?.relative?.lastNumDays ?? 1;
    const startDate =
      activeTimeRange?.kind === TimeRangeKind.RELATIVE_TIME_RANGE
        ? new Date(Date.now() - 86400 * lastNumDays * 1000).getTime()
        : activeTimeRange?.absolute?.fromTimestamp;
    const endDate =
      activeTimeRange?.kind === TimeRangeKind.RELATIVE_TIME_RANGE
        ? new Date().getTime()
        : activeTimeRange?.absolute?.toTimestamp;
    const extraParams = {
      companyId:
        this.view.dom.$searchQueryPanel.companySelector.selectedTarget.companyId ||
        this.dependencies.getActiveCompany().companyId,
      startDate: startDate ?? new Date(Date.now() - 86400 * 1000).getTime(),
      endDate: endDate ?? new Date().getTime(),
      query: query,
    };

    const edrStandardEvents =
      await this.view.dom.$searchResultsPanel.controller.view.dom.$gridView.dataProvider.getEdrStandardEvents({
        extraParams,
      });

    const errorMsgs: Array<string> = [];

    if (edrStandardEvents.errors && edrStandardEvents.errors.length) {
      errorMsgs.push(
        this.dependencies.translateFn('Line {line}, Column {column}', {
          line: edrStandardEvents.errors[0].from,
          column: edrStandardEvents.errors[0].to,
        })
      );

      errorMsgs.push(edrStandardEvents.errors[0].type);
    }

    this.view.dom.$searchQueryPanel.searchQuery.setInputValidation(errorMsgs, true);
    this.view.dom.$searchResultsPanel.controller.view.dom.$gridView.extraParams = extraParams;
    await this.view.dom.$searchResultsPanel.controller.view.dom.$gridView.controller.handleGridItemsLoad();
  }

  private async onUpdateComplete(event: WebComponentEvent) {
    this.buildSearchQuery();

    const codeEditorOptions = this.augmentCodeEditorOptions(CODE_EDITOR_OPTIONS);
    this.view.dom.$searchQueryPanel.searchQuery.setupCodeEditor('lucene', codeEditorOptions.languageOptions, {});
    this.view.dom.$searchQueryPanel.datetimeFilter.setSelectedTimeRange(this.activeTimeRange);
    await this.buildCompanySelectorDropdown();
  }

  private augmentCodeEditorOptions(codeEditorOptions) {
    const fields = codeEditorOptions.languageOptions.schema.fields.items;
    // iterate through fields and check which one has flag that says that it needs completion from backend
    for (const fieldsKey in fields) {
      if (
        fields[fieldsKey].needsBackendCompletion === true ||
        fields[fieldsKey].needsNumericBackendCompletion === true
      ) {
        // if fields have flag, link provision function to it
        fields[fieldsKey].values = {
          provideCompletionItems: async value => {
            const fieldName = fields[fieldsKey].name;
            return {
              incomplete: !fields[fieldsKey].needsNumericBackendCompletion,
              items: await this.getSuggestions(fieldName, value),
            };
          },
          validate: () => true,
        };
      }
    }

    return codeEditorOptions;
  }

  private async getSuggestions(fieldName, fieldValue) {
    const companyId = this.view.dom.$searchQueryPanel.companySelector.selectedTarget.companyId;
    const response = await this.dependencies.api.clients.EdrStandardEvents.getSuggestionsForField(
      {
        fieldName: fieldName,
        fieldValue: fieldValue,
        companyId: companyId,
      },
      getRequestDefaultOptions()
    );

    if (!response || !response.success) {
      return [];
    }
    const formattedResult: {}[] = [];
    for (const suggestion of response.data) {
      let formattedSuggestion = {};
      if (Number.isInteger(suggestion)) {
        formattedSuggestion = {
          name: suggestion.toString(),
          value: suggestion.toString(),
          description: suggestion.toString(),
        };
      } else {
       const suggestionSanitize = XdrSearchController.sanitizeStringValue(suggestion);
        formattedSuggestion = {
          name: suggestion,
          value: suggestionSanitize,
          description: suggestionSanitize,
        };
      }
      formattedResult.push(formattedSuggestion);
    }

    return formattedResult;
  }

  private buildSearchQuery() {
    const initialListItems = [
      {
        groupTitle: this.dependencies.translateFn('Network'),
        prefix: 'network.',
        suffix: ':',
        value: [
          'bytes_in',
          'bytes_out',
          'container_id',
          'container_name',
          'destination_ip',
          'destination_port',
          'direction',
          'file_path',
          'hostname',
          'mac',
          'operation',
          'protocol',
          'request_method',
          'requester_hostname',
          'requester_mac',
          'source_ip',
          'source_port',
          'status_code',
          'stream_type',
          'uri',
          'domain_name',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('Process'),
        prefix: 'process.',
        suffix: ':',
        value: [
          'name',
          'access_privileges',
          'command_line',
          'injection_method',
          'create_type',
          'injection_size',
          'injection_target_path',
          'injection_target_pid',
          'injection_writer_path',
          'injection_writer_pid',
          'integrity_level',
          'module',
          'module_pid',
          'parent_access_privileges',
          'parent_cmdline',
          'parent_integrity_level',
          'parent_path',
          'parent_pid',
          'parent_user',
          'path',
          'pid',
          'sha256',
          'md5',
          'size',
          'user',
          'service_name',
          'service_start_type',
          'is_driver',
          'target_path',
          'target_name',
          'task_name',
          'new_service_name',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('File'),
        prefix: 'file.',
        suffix: ':',
        value: [
          'attribute_operation',
          'destination_file',
          'destination_url',
          'ext',
          'item_type',
          'md5',
          'name',
          'operation',
          'path',
          'old_path',
          'sha256',
          'site',
          'size',
          'type',
          'url',
          'is_remote',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('Registry'),
        prefix: 'registry.',
        suffix: ':',
        value: ['data', 'key', 'operation', 'type', 'value'],
      },
      {
        groupTitle: this.dependencies.translateFn('Email'),
        prefix: 'email.',
        suffix: ':',
        value: [
          'attachments_hashes',
          'attachments_names',
          'attachments_number',
          'attachments_size',
          'attachments_types',
          'attachments_uris',
          'client',
          'date',
          'event_name',
          'id',
          'login_status',
          'logon_type',
          'mailbox_guid',
          'mailbox_owner',
          'origin_ip',
          'parameters.name',
          'parameters.value',
          'path',
          'receiver',
          'receiver_address',
          'receiver_name',
          'sender',
          'sender_address',
          'sender_name',
          'subject',
          'to_address',
          'to_name',
          'from_address',
          'from_name',
          'cc_address',
          'cc_name',
          'bcc_address',
          'bcc_name',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('Alert'),
        prefix: 'alert.',
        suffix: ':',
        value: [
          'id',
          'actions_taken',
          'att&ck_subtechnique',
          'att&ck_subtechnique_id',
          'att&ck_tactic',
          'att&ck_technique',
          'att&ck_technique_id',
          'mark',
          'name',
          'scan_type',
          'severity_score',
          'type',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('User'),
        prefix: 'user.',
        suffix: ':',
        value: [
          'name',
          'domain',
          'email',
          'extended_properties',
          'external_access',
          'id',
          'modified_properties',
          'shared_with',
          'sharing_permissions',
          'target',
          'team_guid',
          'team_members',
          'team_name',
          'type',
          'logon_type',
          'operation',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('Resource'),
        prefix: 'resource.',
        suffix: ':',
        value: [
          'name',
          'type',
          'data',
          'id',
          'app_address',
          'policy_type',
          'url',
          'path',
          'size',
          'md5',
          'sha256',
          'ssh_public_key',
        ],
      },
      {
        groupTitle: this.dependencies.translateFn('Other'),
        prefix: 'other.',
        suffix: ':',
        value: [
          'api',
          'agent',
          'arch',
          'compliance_center_event',
          'detection_class',
          'event_id',
          'event_name',
          'event_type',
          'hostname',
          'ip',
          'organization_id',
          'os',
          'record_type',
          'result_status',
          'rule_id',
          'sensor',
          'script',
          'user',
        ],
      },
    ];
    this.view.dom.$searchQueryPanel.searchQuery.setInitialListItems(initialListItems);
    this.view.dom.$searchQueryPanel.searchQuery.showHint();
  }

  async buildCompanySelectorDropdown() {
    const response = await this.dependencies.api.clients.Company.getAvailableCompanies({options: {
        source: AvailableCompaniesSource.FROM_EDR,
        countRecords: false
      }}, getRequestDefaultOptions());

    const selectedCompany = this.dependencies.selectedCompany;
    if (selectedCompany) {
      this.view.dom.$searchQueryPanel.companySelector.selectedTarget = selectedCompany;
    } else {
      this.view.dom.$searchQueryPanel.companySelector.selectedTarget = this.dependencies.getActiveCompany();
    }
    this.view.dom.$searchQueryPanel.companySelector.label = this.dependencies.translateFn('Company');
    this.view.dom.$searchQueryPanel.companySelector.placeholder = this.dependencies.translateFn('Select company');
    this.view.dom.$searchQueryPanel.companySelector.availableCompanies = response;
    this.view.dom.$searchQueryPanel.companySelector.onCompanyClicked = this.onCompanyClicked.bind(this);

    this.view.dom.$searchQueryPanel.companySelector.injectDependencies(this.dependencies);
    this.view.dom.$searchQueryPanel.companySelector.start().catch(console.error);
  }

  async onCompanyClicked(clickedCompany: Target) {
    this.dependencies.setActiveCompany(clickedCompany.companyId, clickedCompany.companyName);
    this.view.dom.$searchResultsPanel.dispatchEvent(new Event(GlobalExclusionsEvents.REFRESH_COMPONENT));
  }

  public async setCompanyFilter(company: Target) {
    this.view.dom.$searchQueryPanel.setSelectedTarget(company);
    this.view.dom.$searchResultsPanel.dispatchEvent(new Event(GlobalExclusionsEvents.REFRESH_COMPONENT));
    await this.onCompanyChange();
  }

  private getSaveAsPopUpTemplate() {
    const windowBodyHtml = document.createElement('div');
    const windowFooterHtml = document.createElement('div');
    windowBodyHtml.innerHTML = `<p>
        ${this.dependencies.translateFn(
          'This will save the current view as a new entry to the Saved section in the views panel.'
        )}
      </p>
     <gz-form-field label="Name" id="name" class="pt-3" required style="--form-field-label-width: 100px;">
         <gz-input gz-form-input type="text" inputwidth="full" value=""></gz-input>
         <gz-input-error></gz-input-error>
    </gz-form-field>`;

    windowFooterHtml.innerHTML = `<gz-button id='confirm' type="full" color="primary" class='mr-2'>
            ${this.dependencies.translateFn('Save#preserve state')}
        </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;

    return {
      windowBodyHtml,
      windowFooterHtml,
    };
  }

  private onSaveAs() {
    const maxNumberOfCharacters = 64;
    const minNumberOfCharacters = 1;
    const { windowBodyHtml, windowFooterHtml } = this.getSaveAsPopUpTemplate();
    const windowTitle = this.dependencies.translateFn('Save as');
    const saveBtn = windowFooterHtml.querySelector('#confirm') as GzButtonWebComponent;
    const cancelBtn = windowFooterHtml.querySelector('#cancel') as GzButtonWebComponent;
    const name = windowBodyHtml.querySelector('#name') as GzFormFieldWebComponent;
    let formGroup: FormGroup | null;

    name.Validators = [
      RequiredValidator('This field is required'),
      MaxLengthValidator(
        maxNumberOfCharacters,
        this.dependencies.translateFn('The name must have maximum {nameMaxLength} characters.', {
          nameMaxLength: maxNumberOfCharacters,
        })
      ),
      MinLengthValidator(
        minNumberOfCharacters,
        this.dependencies.translateFn('The name must have minimum {nameMinLength} characters.', {
          nameMinLength: minNumberOfCharacters,
        })
      ),
    ];
    formGroup = FormGroup.build({
      name,
    });

    const subscription: Subscription = formGroup.valid$.subscribe({
      next: valid => {
        saveBtn.disabled = !valid;
      },
    });

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: windowTitle,
      bodyContent: windowBodyHtml,
      footerContent: windowFooterHtml,
      isOpen: true,
    });

    saveBtn.addEventListener('click', async () => {
      await this.onConfirmSaveQuery(formGroup, subscription, dialog);
    });

    cancelBtn.addEventListener('click', () => {
      dialog.close();
    });
  }

  private async onConfirmSaveQuery(formGroup, subscription, dialog) {
    const formValues = formGroup?.value;
    const query = await this.view.dom.$searchQueryPanel.searchQuery.getValue();
    const request = {
      companyId: this.dependencies.getActiveCompany().companyId,
      name: htmlEncode(formValues.name),
      query: htmlEncode(query.raw),
    };

    let response: RpcResponse = { success: false };
    try {
      response = await this.dependencies.api.clients.Queries.createRecord(request, getRequestDefaultOptions());
    } catch (err) {
      console.error(err);
      formGroup.children.name.markAsInvalid(err.message);
    }

    if (!response || !response.success) {
      return;
    }

    formGroup?.unsubscribe();
    subscription?.unsubscribe();
    dialog.close();
    await this.createNotification(this.dependencies.translateFn('Successfully saved.'), ToastSeverity.SUCCESS);
    await this.view.loadSmartViewItems(response.data);
    this.selectedSmartViewItem = {
      ...request,
      _id: response.data ?? '',
    };
  }

  private onClickSmartViewItem(event: WebComponentEvent) {
    const queryString = htmlDecode(event.detail.smartViewConfiguration.query);
    this.selectedSmartViewItem = {
      _id: event.detail.smartViewConfiguration.id,
      query: event.detail.smartViewConfiguration.query,
      name: event.detail.smartViewConfiguration.viewName,
      companyId: this.dependencies.getActiveCompany().companyId,
    };
    this.view.dom.$searchQueryPanel.searchQuery.setValue(queryString);
  }

  private async onSave() {
    if (!this.selectedSmartViewItem || !this.selectedSmartViewItem._id) {
      return;
    }

    const query = await this.view.dom.$searchQueryPanel.searchQuery.getValue();
    this.selectedSmartViewItem.query = htmlEncode(query.raw);

    const request = {
      record: this.selectedSmartViewItem,
    };

    let response: RpcResponse = { success: false };
    try {
      response = await this.dependencies.api.clients.Queries.updateRecord(request, getRequestDefaultOptions());
    } catch (err) {
      console.error(err);
    }

    if (!response || !response.success) {
      await this.createNotification(
        this.dependencies.translateFn('Failed to save query. Please try again.'),
        ToastSeverity.ERROR
      );
      await this.view.loadSmartViewItems();
      return;
    }

    await this.createNotification(this.dependencies.translateFn('Successfully saved.'), ToastSeverity.SUCCESS);
    await this.view.loadSmartViewItems(this.selectedSmartViewItem._id);
  }

  private async createNotification(message = '', severity: ToastSeverity) {
    const notificationConfig = {
      message: message,
      hasAction: false,
      severity: severity,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(notificationConfig);
  }

  private async onDeleteSmartViewItem(event: WebComponentEvent) {
    const request = {
      id: event.detail.viewId,
      companyId: this.dependencies.getActiveCompany().companyId,
    };

    let response: RpcResponse = { success: false };
    try {
      response = await this.dependencies.api.clients.Queries.deleteRecord(request, getRequestDefaultOptions());
    } catch (err) {
      console.error(err);
    }
    if (!response || !response.success) {
      await this.createNotification(
        this.dependencies.translateFn('Failed to delete query. Please try again.'),
        ToastSeverity.ERROR
      );
      await this.view.loadSmartViewItems();
      return;
    }

    await this.createNotification(this.dependencies.translateFn('Successfully deleted.'), ToastSeverity.SUCCESS);
    await this.view.loadSmartViewItems();
  }

  private async onCompanyChange() {
    this.selectedSmartViewItem = undefined;
    this.view.dom.$smartViews.controller.view.activeItem = undefined;
    await this.view.loadSmartViewItems();

    const companyId = this.view.dom.$searchQueryPanel.companySelector.selectedTarget.companyId;
    const demoCompanyId = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'Settings',
      method: 'getXdrDemoCompanyId',
      payload: {},
    });
    this.view.dom.$searchQueryPanel.disableSmartViewButtonsForDemoCompany(companyId === demoCompanyId);
  }
}
